import {
  DialogContent,
  Dialog,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import { ZoomOutMapOutlined } from '@material-ui/icons';
import { Color, Inline } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import defaultImage from 'shared/icons/assets/black_default_image.png';
import { ErrorIcon } from 'shared/icons/ErrorIcon';
import { formatDuration, InlinePhoto } from 'shared/utils/MediaUtils';
import styled from 'styled-components';

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PhotoGridItem = styled.button`
  position: relative;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  img {
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
    object-fit: contain;
    border-radius: 4px;
    transition: transform 0.2s ease;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const NonVehiclePhotoBadge = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
`;

const VideoDuration = styled.div`
  z-index: 2;
  position: absolute;
  bottom: 4px;
  right: 4px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  padding: 0 2px;
  color: ${Color.White};
  font-size: 11px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledDialogContent = styled(DialogContent)`
  background-color: ${Color.Black};
`;

const StyledDialogTitle = styled(DialogTitle)`
  background-color: ${Color.Black};
`;

interface PhotoViewModeProps {
  open: boolean;
  onClose: () => void;
  inlinePhotos: InlinePhoto[];
  onSelect: (index: number) => void;
}

interface PhotoItemProps {
  photo: InlinePhoto;
  index: number;
  onSelect: (index: number) => void;
}

function PhotoItem({ photo, index, onSelect }: PhotoItemProps) {
  return (
    <PhotoGridItem
      type="button"
      aria-label={`Open photo ${index + 1}`}
      onClick={() => {
        onSelect(index);
      }}
    >
      <img
        src={photo.url}
        alt={photo.thumbnail_url}
        onError={(e) => {
          e.currentTarget.src = defaultImage;
        }}
      />
      {photo.is_video &&
        photo.duration_in_seconds &&
        photo.duration_in_seconds > 0 && (
          <VideoDuration>
            {formatDuration(photo.duration_in_seconds)}
          </VideoDuration>
        )}
      {photo.is_vehicle_detected === false && (
        <NonVehiclePhotoBadge aria-label="Non-vehicle photo badge">
          <ErrorIcon />
        </NonVehiclePhotoBadge>
      )}
    </PhotoGridItem>
  );
}

export function PhotoViewMode({
  open,
  onClose,
  inlinePhotos,
  onSelect,
}: PhotoViewModeProps) {
  return (
    <Dialog open={open} fullScreen={true} onClose={onClose}>
      <StyledDialogTitle>
        <Inline horizontalAlign="right">
          <IconButton aria-label="Close grid view" onClick={onClose}>
            <ZoomOutMapOutlined />
          </IconButton>
        </Inline>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Container>
          <Box width="90%" maxWidth="1200px">
            <PhotoGrid>
              {inlinePhotos.map((photo, index) => (
                <PhotoItem
                  key={photo.id}
                  photo={photo}
                  index={index}
                  onSelect={onSelect}
                />
              ))}
            </PhotoGrid>
          </Box>
        </Container>
      </StyledDialogContent>
    </Dialog>
  );
}
